import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api';
import { Card, EmptyState, ErrorBanner, Loading } from '../components/ui';
import { idiomaActual, useT } from '../i18n';

/**
 * Lo que se cobró en Telegram, entre todas las influencers y por cada una.
 *
 * ── Por qué las tres cifras están siempre juntas ──────────────────────────────
 * Porque por separado engañan, igual que en las páginas. Las ventas no distinguen un fan que
 * compró cinco fotos de cinco personas que compraron una; las Stars no dicen sobre cuánta gente;
 * los compradores no dicen cuánto entró. Juntas contestan la pregunta entera — y la diferencia
 * entre las dos primeras es la que decide si conviene subir el precio o publicar más.
 *
 * ── Por qué se ve una sola foto por fila y no un gráfico ──────────────────────
 * Porque la pregunta que se le hace a esta pantalla es «cuál vendió», y eso se contesta mirando.
 * Un identificador de publicación obligaría a ir a buscarla a otra pantalla para saber cuál era.
 */
type Datos = {
  creatorId: string | null;
  porInfluencer: {
    creatorId: string;
    publicName: string;
    ventas: number;
    stars: number;
    compradores: number;
  }[];
  sinAtribuir: number;
  ventas: {
    id: string;
    creatorId: string | null;
    contentItemId: string | null;
    topic: string | null;
    compradorId: string;
    compradorUsuario: string | null;
    stars: number;
    compradaEn: string;
    miniatura: string | null;
  }[];
};

export function Ventas() {
  const { t } = useT();
  /**
   * Sobre qué influencer se mira, igual que en Páginas. Vacío es «todas».
   *
   * El filtro lo aplica el SERVIDOR: la lista viene ya recortada y el alcance por dueño va dentro
   * de la consulta, así que acá no hay nada que volver a agrupar ni que comprobar.
   */
  const [creatorId, setCreatorId] = useState('');
  const state = useApi<Datos>(
    `/api/ventas/estadisticas${creatorId ? `?creator=${creatorId}` : ''}`,
    60_000,
  );

  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading label={t('ventas.contando')} />;

  const datos = state.data;
  const elegida = datos.porInfluencer.find((fila) => fila.creatorId === datos.creatorId);

  /*
    Con una influencer elegida, las cifras de arriba son las SUYAS y no la suma.

    Se toman de su fila y no se recalculan sobre la lista: la lista está cortada en cincuenta, así
    que sumarla daría un total más chico que el real en cuanto haya más ventas que ésas.
  */
  const total = elegida ?? {
    ventas: datos.porInfluencer.reduce((suma, fila) => suma + fila.ventas, 0),
    stars: datos.porInfluencer.reduce((suma, fila) => suma + fila.stars, 0),
    /*
      Los compradores NO se suman entre influencers.

      La misma persona puede comprarle a dos, y sumando contaría dos veces. Lo que se puede decir
      sin mentir es cuántos distintos hay en las ventas que se están mostrando, así que el total
      general dice eso y la fila de cada influencer dice los suyos.
    */
    compradores: new Set(datos.ventas.map((venta) => venta.compradorId)).size,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t('ventas.titulo')}</h1>
          <p className="mt-0.5 text-sm text-[var(--color-ink-muted)]">{t('ventas.bajada')}</p>
        </div>
        {datos.porInfluencer.length > 1 && (
          <select
            value={creatorId}
            onChange={(event) => setCreatorId(event.target.value)}
            aria-label={t('ventas.porInfluencer')}
            className="rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5 text-sm"
          >
            <option value="">{t('paginas.todasLasInfluencers')}</option>
            {datos.porInfluencer.map((fila) => (
              <option key={fila.creatorId} value={fila.creatorId}>
                {fila.publicName}
              </option>
            ))}
          </select>
        )}
      </div>

      {datos.porInfluencer.length === 0 ? (
        <EmptyState title={t('ventas.ninguna')} description={t('ventas.ningunaQue')} />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            {[
              { titulo: t('ventas.cuantas'), valor: total.ventas, pie: t('ventas.cuantasQue') },
              { titulo: t('ventas.enStars'), valor: total.stars, pie: '⭐' },
              {
                titulo: t('ventas.compradores'),
                valor: total.compradores,
                pie: t('ventas.compradoresQue'),
              },
            ].map((dato) => (
              <Card key={dato.titulo}>
                <p className="text-xs text-[var(--color-ink-muted)]">{dato.titulo}</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {dato.valor.toLocaleString(idiomaActual())}
                </p>
                <p className="mt-0.5 text-xs text-[var(--color-ink-faint)]">{dato.pie}</p>
              </Card>
            ))}
          </div>

          {/*
            Las que no se pudieron atribuir, dichas donde se miran las ventas.

            Se guardan a propósito cuando el payload no se puede resolver —el dinero entró— pero
            como todo lo demás va agrupado por influencer, sin esta línea se guardarían para
            nadie. Sólo aparece si no es cero: un cero permanente entrena a no leerlo.
          */}
          {datos.sinAtribuir > 0 && (
            <p className="text-sm font-medium text-[var(--color-warn)]">
              {t('ventas.sinAtribuir', { n: datos.sinAtribuir })}
            </p>
          )}

          {/*
            La comparación entre influencers, que es lo que una ficha no puede contestar.

            «A cuál le va mejor» necesita verlas juntas; con una pestaña por influencer nadie
            compara nada. Es el mismo argumento que ya justifica la pantalla de Plataformas.
          */}
          <Card title={t('ventas.porInfluencer')}>
            <ul className="divide-y divide-[var(--color-border-subtle)]">
              {datos.porInfluencer.map((fila) => (
                <li
                  key={fila.creatorId}
                  className={
                    'flex flex-wrap items-center gap-3 py-2.5 ' +
                    (fila.creatorId === datos.creatorId
                      ? '-mx-2 rounded-lg bg-[var(--color-accent-soft)] px-2'
                      : '')
                  }
                >
                  <Link
                    to={`/influencer/${fila.creatorId}`}
                    className="min-w-0 flex-1 truncate text-sm font-medium hover:text-[var(--color-accent)]"
                  >
                    {fila.publicName}
                  </Link>
                  <span className="shrink-0 text-xs tabular-nums text-[var(--color-ink-muted)]">
                    {t('ventas.filaInfluencer', {
                      ventas: fila.ventas,
                      compradores: fila.compradores,
                    })}
                  </span>
                  <span className="w-20 shrink-0 text-right text-sm font-semibold tabular-nums text-[var(--color-accent)]">
                    {fila.stars} ⭐
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          <Card title={t('ventas.listaTitulo')}>
            <ul className="divide-y divide-[var(--color-border-subtle)]">
              {datos.ventas.map((venta) => (
                <li key={venta.id} className="flex items-center gap-3 py-2.5">
                  {venta.miniatura ? (
                    <img
                      src={venta.miniatura}
                      alt=""
                      className="h-12 w-12 shrink-0 rounded-lg object-cover"
                    />
                  ) : (
                    /* La publicación ya no existe. La venta sí, y se muestra igual. */
                    <span className="h-12 w-12 shrink-0 rounded-lg bg-[var(--color-surface-sunken)]" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {venta.topic ?? t('ventas.sinPublicacion')}
                    </p>
                    <p className="text-xs text-[var(--color-ink-muted)]">
                      {t('ventas.compradaPor', {
                        quien: venta.compradorUsuario
                          ? `@${venta.compradorUsuario}`
                          : venta.compradorId,
                        fecha: new Date(venta.compradaEn).toLocaleString(),
                      })}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-[var(--color-accent)]">
                    {venta.stars} ⭐
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </div>
  );
}
